import { useUnlimitedContext } from '../contexts/UnlimitedContext';
import { HelperPills } from '../HelperPills';

/**
 * Displays helper pill buttons and an autocomplete suggestion list below the Unlimited prompt input.
 * Reads all required state from UnlimitedContext — accepts no props.
 */
export const SuggestionsPanel = () => {
  const { input, suggestions, autocomplete } = useUnlimitedContext();
  const { query } = input;
  const { userPills, pillBounds, pillsRef } = suggestions;
  const {
    activePill,
    handlePillClick,
    isPillLoading,
    displayedSuggestions,
    selectedIndex,
    selectSuggestion,
    highlightMatch,
    libraryPushedDown,
  } = autocomplete;

  return (
    <div
      className={
        libraryPushedDown
          ? 'min-h-[19rem]'
          : 'min-h-[7.5rem] transition-[min-height] duration-[550ms] ease-in-out'
      }>
      {/* Helper Pills -- always mounted, animated collapse when typing.
          Below md, pills wrap to multiple rows and HelperPills removes its own
          row-cap — so the outer wrapper must allow the wrapped height or rows
          2+ get clipped. Desktop keeps the single-row max-h-24 cap. */}
      {userPills.length > 0 && (
        <div
          className={`transition-opacity duration-200 overflow-hidden ${
            query.trim()
              ? `opacity-0 pointer-events-none ${libraryPushedDown ? 'max-h-0' : 'max-h-56 md:max-h-24'}`
              : 'opacity-100 max-h-56 md:max-h-24'
          }`}>
          <HelperPills
            ref={pillsRef}
            pills={userPills}
            activePill={activePill}
            onPillClick={handlePillClick}
          />
        </div>
      )}

      {/* Suggestions */}
      <div>
        {/* Skeleton loading for pill suggestions */}
        {isPillLoading && displayedSuggestions.length === 0 && (
          <div
            className={`mt-4 divide-y divide-gray-100 ${!pillBounds ? 'mx-2' : ''}`}
            style={
              pillBounds
                ? { paddingLeft: pillBounds.left, paddingRight: pillBounds.right }
                : undefined
            }>
            {/* Varied widths so skeletons read as text lines, not uniform shapes */}
            {[85, 70, 90, 75].map((width, index) => (
              <div key={index} className='px-4 py-3'>
                <div
                  className='h-4 my-1 rounded-lg animate-shimmer'
                  style={{
                    width: `${width}%`,
                    backgroundImage: 'linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 45%, #f3f4f6 60%)',
                    backgroundSize: '200% 100%',
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Inline Suggestions List */}
        <div
          className={`mt-4 divide-y divide-gray-100 transition-opacity duration-200 ${
            displayedSuggestions.length > 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'
          } ${!pillBounds ? 'mx-2' : ''}`}
          style={
            activePill && pillBounds
              ? { paddingLeft: pillBounds.left, paddingRight: pillBounds.right }
              : undefined
          }>
          {displayedSuggestions.slice(0, 4).map((suggestion, index) => (
            <button
              key={index}
              onClick={() => selectSuggestion(suggestion)}
              className={`group/suggestion w-full px-4 py-3 text-left rounded-xl ${
                index === selectedIndex ? 'bg-gray-100' : 'hover:bg-gray-100'
              }`}>
              <span
                className={
                  query.trim()
                    ? 'text-gray-900'
                    : 'text-gray-500 group-hover/suggestion:text-gray-900'
                }>
                {highlightMatch(suggestion.text, query)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
